import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { createContext, useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loading } from "@/components/atoms";
import { SignUpStep, UserType, UserTypeData } from "@/components/organisms";
import { userTypes } from "@/components/organisms/SignUp/fixtures";
import { NotFound } from "@/components/pages";
import { useProviderCode, useSiteConfig } from "@/hooks";
import { useLazyVerifyProviderQuery } from "@/redux/apis/auth/authApi";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { setActiveOperator } from "@/redux/slices/operator/operatorSlice";
import { activeOperatorSelector } from "@/redux/slices/operator/selectors";

interface SignUpState {
  userTypeData?: UserTypeData;
  setUserTypeData: React.Dispatch<React.SetStateAction<UserTypeData | undefined>>;
  step: SignUpStep;
  setStep: React.Dispatch<React.SetStateAction<SignUpStep>>;
  formStep: number;
  setFormStep: React.Dispatch<React.SetStateAction<number>>;
}

const SignUpContext = createContext<SignUpState>({
  userTypeData: undefined,
  setUserTypeData: () => ({}),
  step: "user-select",
  setStep: () => ({}),
  formStep: 1,
  setFormStep: () => ({}),
});

export const SignUpContextProvider = ({ children }: React.PropsWithChildren) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { domain, apiUrl } = useSiteConfig();
  const providerCode = useProviderCode();
  const [params] = useSearchParams();

  const [verify, { isError }] = useLazyVerifyProviderQuery();
  const operator = useAppSelector(activeOperatorSelector);
  const { isAuthenticated, logout } = useKindeAuth();

  const paramsUserType = params.get("type");
  // An EA-invited non-user arrives with an `email` param on the CTA; that case is handled by
  // UserSelectForm (auto-fires the passenger Kinde register), so it must NOT take the legacy
  // singleTenant `?type=` shortcut below (which routes into the non-Kinde SignUpFormClient).
  const invitedEmail = params.get("email")?.trim();
  const [userTypeData, setUserTypeData] = useState<UserTypeData>();
  const [step, setStep] = useState<SignUpStep>("user-select");
  const [formStep, setFormStep] = useState(1);
  const userTypeList = Object.keys(userTypes);

  useEffect(() => {
    if (isAuthenticated) {
      // Trigger Kinde logout to ensure user starts fresh
      logout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (operator) {
      const { singleTenant } = operator;

      if (paramsUserType && !invitedEmail) {
        // Disable automatic signup for operator and driver types - they should use dedicated routes instead
        const disabledTypes = ["operator", "driver"];
        if (!disabledTypes.includes(paramsUserType) && userTypeList.includes(paramsUserType) && singleTenant) {
          const userType = userTypes[paramsUserType as UserType];
          setUserTypeData(userType);
          setStep("signup");
        }
      }
    } else {
      verify({ providerCode: providerCode || domain, apiUrl, forDriver: paramsUserType === "driver" })
        .unwrap()
        .then((operator) => dispatch(setActiveOperator(operator)))
        .catch((e) => {
          console.warn("(Signup) Error verifying provider: ", {
            providerCode: providerCode || domain,
            error: e,
          });

          navigate("/404");
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operator]);

  if (isError) return <NotFound />;
  if (!operator) return <Loading />;

  return (
    <SignUpContext.Provider
      value={{
        userTypeData,
        setUserTypeData,
        step,
        setStep,
        formStep,
        setFormStep,
      }}
    >
      {children}
    </SignUpContext.Provider>
  );
};

export const useSignUpContext = () => {
  const context = useContext(SignUpContext);
  if (!context) throw new Error("useSignUpContext must be used within a SignUpContextProvider");

  return context;
};
